/**
 * Anmeldung.
 *
 * Zweispaltig: links das Eichenherz-Logo und darunter Kora, rechts die
 * Personenwahl. Der zweite Schritt ist kein Passwortfeld, sondern die
 * Weiterleitung zur Microsoft-Anmeldung – das Passwort gehört zum Konto.
 *
 * Kora tritt hier als Person auf, und nur hier: Das Bild trägt Wortzeichen,
 * Auflösung und Begrüßung. Es ist die einzige Stelle im ganzen System mit
 * einer ganzen Figur – überall sonst gibt es höchstens ein Portrait.
 *
 * Das Layout ist ein Raster mit zwei Zeilen: In der ersten steht links das
 * Logo, in der zweiten links das Bild und rechts das Formular. Dadurch
 * beginnen Bild und „Anmelden" auf derselben Linie, ohne dass irgendwo eine
 * Höhe von Hand nachgerechnet werden müsste.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useKora } from '../zustand';
import type { LoginTeam, Nutzer } from '../typen';
import './login.css';

export function Login() {
  const { nachAnmeldung } = useKora();
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [fehler, setFehler] = useState('');
  const [laeuft, setLaeuft] = useState(false);

  const abfrage = useQuery({
    queryKey: ['login-team'],
    queryFn: () => api.hole<LoginTeam>('/auth/team'),
    retry: 1,
  });

  const demoModus = abfrage.data?.modus === 'demo';
  const team = abfrage.data?.team ?? [];

  async function anmelden(name: string) {
    setGewaehlt(name);
    setFehler('');

    if (!demoModus) {
      // Microsoft übernimmt: eigene Seite, deshalb harte Weiterleitung.
      window.location.assign(`/api/auth/login?name=${encodeURIComponent(name)}`);
      return;
    }

    setLaeuft(true);
    try {
      const nutzer = await api.sende<Nutzer>('/auth/demo', { name });
      nachAnmeldung(nutzer);
    } catch (f) {
      setFehler(f instanceof Error ? f.message : 'Die Anmeldung ist fehlgeschlagen.');
      setGewaehlt(null);
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <div className="login">
      <div className="login-logozeile">
        <img
          src="/eichenherz-logo.png"
          alt="Eichenherz – Weil es Ihr Zuhause ist"
          className="login-logo"
        />
      </div>
      {/* Hält die Kopfzeile rechts offen, damit beide Spalten auf einer Linie beginnen. */}
      <div className="login-logozeile login-logozeile-rechts" aria-hidden="true" />

      <div className="login-marke">
        {/*
          Der Ausschnitt ist gerechnet, nicht geschätzt: Die Oberkante der
          Haare liegt bei 3,03 % der Bildhöhe. Der Rahmen hat das Verhältnis
          1536:993, das Bild sitzt um 3,12 % der Rahmenhöhe höher – dann
          liegen Haaransatz, Oberkante des Wortzeichens und „Anmelden" rechts
          auf einer Linie.
        */}
        <div className="login-bild">
          {/*
            WebP mit PNG als Rückfallebene: 44 statt 252 kB, und dies ist das
            erste Bild, das jemand von Kora sieht – noch vor der Anmeldung.
          */}
          <picture>
            <source srcSet="/kora-login.webp" type="image/webp" />
            <img
              src="/kora-login.png"
              alt="Kora – Kunden, Offerte, Rechnungen, Auswertungen. Guten Tag. Die Zahlen liegen schon bereit."
            />
          </picture>
        </div>
        <div className="login-fuss">Internes Werkzeug der Eichenherz GmbH</div>
      </div>

      <div className="login-formular">
        <div className="login-block">
          <div>
            <div className="eh-kicker">Anmelden</div>
            <div className="login-frage">Wer arbeitet gerade?</div>
          </div>

          {abfrage.isLoading && <div className="eh-hinweis">Team wird geladen …</div>}

          {abfrage.isError && (
            <div className="eh-banner eh-banner--rot">
              Die Anwendung ist gerade nicht erreichbar. Bitte die Seite neu laden.
            </div>
          )}

          <div className="login-liste">
            {team.map((person) => (
              <button
                key={person.name}
                type="button"
                className="login-person"
                onClick={() => void anmelden(person.name)}
                disabled={laeuft}
              >
                <span
                  className="eh-avatar login-avatar"
                  style={
                    person.profilbild
                      ? { backgroundImage: `url(${person.profilbild})` }
                      : undefined
                  }
                >
                  {person.profilbild ? '' : person.initialen}
                </span>
                <span className="login-name">{person.name}</span>
                <span className="login-chevron">
                  {gewaehlt === person.name && laeuft ? '…' : '›'}
                </span>
              </button>
            ))}
          </div>

          {fehler && <div className="eh-fehler">{fehler}</div>}

          {gewaehlt && !demoModus && (
            <div className="eh-hinweis">Weiterleitung zur Microsoft-Anmeldung …</div>
          )}

          <div className="login-hinweis">
            {demoModus ? (
              <>
                Demo-Modus: die Auswahl meldet direkt an, ohne Microsoft-Konto. Für den Echtbetrieb
                in der Konfiguration <code>AUTH_MODE=entra</code> setzen.
              </>
            ) : (
              <>
                Die Anmeldung läuft über das Microsoft-Konto von Eichenherz. Zugänge verwaltet die
                Geschäftsführung im Admin Center.
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
